import { create } from 'zustand';
import type {
  IntegritySymbolStatus,
  IntegrityRunDetail,
  IntegrityServiceHealth,
  IntegrityAlert,
  ReadinessRegistryEntry,
  IncidentReport,
  FreshnessSnapshot,
} from '../api/types';

/**
 * Aggregated readiness snapshot for a single (market, symbol). Derived from
 * the per-range `ReadinessRegistryEntry` rows on every mutation so selectors
 * can return primitives without a `useMemo` dance.
 */
export type SymbolReadinessState = {
  fullyVerified: boolean;
  anyCorrupt: boolean;
  anyAcceptedGap: boolean;
  anyRepairing: boolean;
  total: number;
};

/** Kinds of chart gap range the PriceChart renders as red overlays. */
export type ChartGapKind = 'corrupt' | 'accepted-gap' | 'repairing';

export type ChartGapRange = {
  fromTs: number;
  toTs: number;
  kind: ChartGapKind;
};

const EMPTY_REGISTRY: ReadinessRegistryEntry[] = [];
const EMPTY_INCIDENTS: IncidentReport[] = [];
const EMPTY_RANGES: ChartGapRange[] = [];

function deriveReadinessState(
  entries: ReadinessRegistryEntry[],
): SymbolReadinessState {
  let fully = entries.length > 0;
  let anyCorrupt = false;
  let anyAccepted = false;
  let anyRepair = false;
  for (const e of entries) {
    if (e.repairInProgress) anyRepair = true;
    switch (e.status) {
      case 'corrupt':
      case 'bar-invalid':
      case 'aggregate-value-mismatch':
      case 'serve-broken':
        anyCorrupt = true;
        fully = false;
        break;
      case 'accepted-gap':
      case 'accepted-outage':
        anyAccepted = true;
        break;
      case 'verified':
      case 'healthy':
      case 'confirmed':
        // stays fully
        break;
      case 'repairing':
        anyRepair = true;
        fully = false;
        break;
      default:
        fully = false;
    }
  }
  return {
    fullyVerified: fully && !anyCorrupt && !anyRepair,
    anyCorrupt,
    anyAcceptedGap: anyAccepted,
    anyRepairing: anyRepair,
    total: entries.length,
  };
}

export type IntegrityViewMode = 'recent' | 'allTime';

type IntegrityState = {
  /** Per-symbol integrity status, keyed by symbol code */
  statuses: Record<string, IntegritySymbolStatus>;
  /** Monotonic version counter — bumped only when data actually changes */
  version: number;
  /** When the last audit was received */
  lastUpdateAt: number | null;
  /** Last scan type received */
  lastScanType: 'frontier' | 'deep' | 'daily' | null;
  /** Which tier to show in the UI */
  viewMode: IntegrityViewMode;
  /** Symbols currently being repaired (persists across page navigation) */
  repairingSymbols: Set<string>;
  /** Whether a "repair all" is in progress */
  repairingAll: boolean;
  /** Last 10 audit runs with full data */
  runs: IntegrityRunDetail[];
  /** Selected run ID — null means "Live" view */
  selectedRunId: string | null;
  /** Active aggregate-flat repair alerts, keyed by digest. */
  alerts: Record<string, IntegrityAlert>;
  /** Resolved-event timestamps, keyed by digest. UI uses this to swap to "data restored" briefly before auto-dismiss. */
  resolvedAt: Record<string, number>;
  /**
   * Last-known alert payload by digest, kept across resolution so the
   * "data restored" banner can render with full context after the alert has
   * been removed from `alerts`. Cleared by `dismissResolved`.
   */
  resolvedAlertCache: Record<string, IntegrityAlert>;
  /** Service health diagnostics (from WS integrity-health messages) */
  health: IntegrityServiceHealth | null;
  /**
   * When `health` was last populated. Used with HEALTH_FRESHNESS_MS to
   * distinguish "just received an update" from "connection dropped and this
   * is cached stale data". Null before any health message arrives.
   */
  healthLastUpdateAt: number | null;
  /** Merge from WS integrity-update messages (additive) */
  setStatuses: (symbols: IntegritySymbolStatus[]) => void;
  /** Replace from WS integrity-snapshot messages (full replacement) */
  replaceStatuses: (symbols: IntegritySymbolStatus[]) => void;
  setViewMode: (mode: IntegrityViewMode) => void;
  markRepairing: (symbol: string) => void;
  clearRepairing: (symbol: string) => void;
  setRepairingAll: (v: boolean) => void;
  setRuns: (runs: IntegrityRunDetail[]) => void;
  addRun: (run: IntegrityRunDetail) => void;
  selectRun: (runId: string | null) => void;
  /** Add or refresh an alert (idempotent on digest). */
  addAlert: (alert: IntegrityAlert) => void;
  /** Mark alert resolved; UI will auto-dismiss after AUTO_DISMISS_MS. */
  resolveAlert: (priorDigest: string) => void;
  /** Bulk replace alerts (used by integrity-snapshot on subscribe). */
  setAlerts: (alerts: IntegrityAlert[]) => void;
  /** Drop a resolved alert from the resolvedAt map after the auto-dismiss timer fires. */
  dismissResolved: (digest: string) => void;
  setHealth: (health: IntegrityServiceHealth) => void;
  /**
   * Drop cached integrity health (e.g. on WS disconnect). The previous
   * implementation left stale `health` in place across a full WS teardown,
   * so `HealthSummary` would keep reporting data-integrity as online even
   * after the service vanished.
   */
  clearHealth: () => void;
  /** Per-range readiness entries, keyed by `${market}:${symbol}:${interval}:${rangeStart}-${rangeEnd}`. */
  readinessRegistry: Record<string, ReadinessRegistryEntry>;
  /** Aggregated state per (market, symbol). Keyed by `${market}:${symbol}`. */
  readinessBySymbol: Record<string, SymbolReadinessState>;
  /** Recent incidents (ring buffer, newest first, max 100). */
  incidents: IncidentReport[];
  /**
   * Latest per-symbol freshness snapshot (ticket 075) — "is data arriving
   * right now", which `statuses` (a 24h completeness window) cannot answer.
   * Whole-snapshot replacement, never merged: the producer sends the complete
   * dark set every tick, so merging could leave a recovered symbol shown dark
   * forever. null = the detector has not reported yet.
   */
  freshness: FreshnessSnapshot | null;
  upsertReadiness: (entry: ReadinessRegistryEntry) => void;
  replaceReadiness: (entries: ReadinessRegistryEntry[]) => void;
  addIncident: (incident: IncidentReport) => void;
  /** Apply a resolution to an existing incident by incidentId (patch resolvedAt in-place). */
  resolveIncident: (incidentId: string, resolvedAt: number) => void;
  setIncidents: (incidents: IncidentReport[]) => void;
  setFreshness: (freshness: FreshnessSnapshot) => void;
};

/**
 * Maximum age of a health message before it is considered stale. The server
 * publishes an integrity-health snapshot on a regular cadence (~30s), so 90s
 * gives 3 missed snapshots before we flip the UI offline — robust to a
 * dropped packet or a single slow scan while still catching a real outage.
 */
export const INTEGRITY_HEALTH_FRESHNESS_MS = 90_000;

/**
 * Pure selector used by `HealthSummary` and anywhere else that needs to know
 * whether the data-integrity service is considered online. Returns true iff
 * we have a health snapshot AND it's within the freshness window.
 */
export function isIntegrityHealthFresh(
  state: Pick<IntegrityState, 'health' | 'healthLastUpdateAt'>,
  nowMs: number = Date.now(),
  freshnessMs: number = INTEGRITY_HEALTH_FRESHNESS_MS,
): boolean {
  if (state.health == null) return false;
  if (state.healthLastUpdateAt == null) return false;
  return nowMs - state.healthLastUpdateAt <= freshnessMs;
}

const EMPTY_STATUSES: Record<string, IntegritySymbolStatus> = {};
const EMPTY_ALERTS: IntegrityAlert[] = [];

/**
 * Stale alert TTL: alerts whose `emittedAt` is older than this are filtered
 * out of `useAlertsForSymbol` and culled by `pruneStaleAlerts`. They're
 * almost always orphaned by a missed `integrity-resolved` event (network
 * blip, gateway restart, dispatcher race) — they stop reflecting reality
 * within minutes but the UI used to keep showing the chip indefinitely.
 *
 * 10 min is conservative — most repairs complete in <30s; a single repair
 * that legitimately runs past 10 min is rare but already past its useful
 * "in flight" UX moment, so dimming it is preferable to lying about state.
 */
const STALE_ALERT_TTL_MS = 10 * 60_000;

/**
 * Hook: return the active repair alerts for a specific symbol, filtered
 * to fresh alerts only. Stale alerts (older than `STALE_ALERT_TTL_MS`)
 * are excluded so chips auto-clear when a `integrity-resolved` event is
 * dropped. Safe for use inside `memo()` components — the selector
 * returns a stable reference to the raw `alerts` map and we apply the
 * filter inline. The naive `(s) => s.alerts.filter(...)` produces a new
 * array every render and triggers Zustand infinite re-render.
 */
export function useAlertsForSymbol(
  symbol: string | null | undefined,
  altSymbol?: string | null | undefined,
): IntegrityAlert[] {
  const alerts = useIntegrityStore((s) => s.alerts);
  if (!symbol && !altSymbol) return EMPTY_ALERTS;
  const cutoff = Date.now() - STALE_ALERT_TTL_MS;
  const matches: IntegrityAlert[] = [];
  for (const a of Object.values(alerts)) {
    if (a.emittedAt < cutoff) continue;
    if (a.symbol === symbol || (altSymbol != null && a.symbol === altSymbol)) {
      matches.push(a);
    }
  }
  return matches.length > 0 ? matches : EMPTY_ALERTS;
}

/** Distinct symbols with at least one fresh active repair — call inside useMemo. */
export function computeRepairingSymbolSet(
  alerts: Record<string, IntegrityAlert>,
): Set<string> {
  const cutoff = Date.now() - STALE_ALERT_TTL_MS;
  const s = new Set<string>();
  for (const a of Object.values(alerts)) {
    if (a.emittedAt < cutoff) continue;
    s.add(a.symbol);
  }
  return s;
}

function timeframesChanged(
  a: IntegritySymbolStatus['timeframes'] | undefined,
  b: IntegritySymbolStatus['timeframes'] | undefined,
): boolean {
  if (a === b) return false;
  if (!a || !b) return a !== b;
  if (a.length !== b.length) return true;
  return a.some((tf, index) => (
    tf.interval !== b[index]?.interval ||
    tf.ohlcPercent !== b[index]?.ohlcPercent ||
    tf.flatBarDensity !== b[index]?.flatBarDensity ||
    tf.syntheticDominantBars !== b[index]?.syntheticDominantBars ||
    tf.status !== b[index]?.status
  ));
}

function gapsChanged(
  a: IntegritySymbolStatus['gaps'],
  b: IntegritySymbolStatus['gaps'],
): boolean {
  if (a === b) return false;
  if (a.length !== b.length) return true;
  return a.some((gap, index) => (
    gap.fromTs !== b[index]?.fromTs ||
    gap.toTs !== b[index]?.toTs ||
    gap.missingCandles !== b[index]?.missingCandles
  ));
}

function sourceBreakdownChanged(
  a: IntegritySymbolStatus['sourceBreakdown'] | undefined,
  b: IntegritySymbolStatus['sourceBreakdown'] | undefined,
): boolean {
  if (a === b) return false;
  if (!a || !b) return a !== b;
  return (
    a.ws !== b.ws ||
    a.rest !== b.rest ||
    a.bulk !== b.bulk ||
    a.synthetic !== b.synthetic
  );
}

function tieredChanged(
  a: IntegritySymbolStatus['tiered'] | undefined,
  b: IntegritySymbolStatus['tiered'] | undefined,
): boolean {
  if (a === b) return false;
  if (!a || !b) return a !== b;
  return (
    a.recent.windowMs !== b.recent.windowMs ||
    a.recent.ohlcCompletenessPercent !== b.recent.ohlcCompletenessPercent ||
    timeframesChanged(a.recent.timeframes, b.recent.timeframes) ||
    a.allTime.ohlcCompletenessPercent !== b.allTime.ohlcCompletenessPercent ||
    a.allTime.totalChunksWithData !== b.allTime.totalChunksWithData ||
    timeframesChanged(a.allTime.timeframes, b.allTime.timeframes)
  );
}

/** Compare the fields the integrity UI actually renders or acts on. */
function statusChanged(
  a: IntegritySymbolStatus | undefined,
  b: IntegritySymbolStatus,
): boolean {
  if (!a) return true;
  return (
    a.ohlcCompletenessPercent !== b.ohlcCompletenessPercent ||
    a.ohlcRealCompletenessPercent !== b.ohlcRealCompletenessPercent ||
    a.confirmedChunks !== b.confirmedChunks ||
    a.totalChunks !== b.totalChunks ||
    a.partialChunks !== b.partialChunks ||
    a.unconfirmedChunks !== b.unconfirmedChunks ||
    a.gapCount !== b.gapCount ||
    gapsChanged(a.gaps, b.gaps) ||
    timeframesChanged(a.timeframes, b.timeframes) ||
    sourceBreakdownChanged(a.sourceBreakdown, b.sourceBreakdown) ||
    tieredChanged(a.tiered, b.tiered)
  );
}

const MAX_RUNS = 10;

export const useIntegrityStore = create<IntegrityState>()((set) => ({
  statuses: EMPTY_STATUSES,
  version: 0,
  lastUpdateAt: null,
  lastScanType: null,
  viewMode: 'recent',
  repairingSymbols: new Set<string>(),
  repairingAll: false,
  runs: [],
  selectedRunId: null,
  health: null,
  healthLastUpdateAt: null,
  alerts: {},
  resolvedAt: {},
  resolvedAlertCache: {},
  readinessRegistry: {},
  readinessBySymbol: {},
  incidents: EMPTY_INCIDENTS,
  freshness: null,
  upsertReadiness: (entry) =>
    set((state) => {
      const key = `${entry.market}:${entry.symbol}:${entry.interval}:${entry.rangeStart}-${entry.rangeEnd}`;
      const nextRegistry = { ...state.readinessRegistry, [key]: entry };
      const symbolKey = `${entry.market}:${entry.symbol}`;
      const peers = Object.values(nextRegistry).filter(
        (e) => e.market === entry.market && e.symbol === entry.symbol,
      );
      return {
        readinessRegistry: nextRegistry,
        readinessBySymbol: {
          ...state.readinessBySymbol,
          [symbolKey]: deriveReadinessState(peers),
        },
      };
    }),
  replaceReadiness: (entries) =>
    set(() => {
      const registry: Record<string, ReadinessRegistryEntry> = {};
      const bySymbolEntries: Record<string, ReadinessRegistryEntry[]> = {};
      for (const e of entries) {
        const key = `${e.market}:${e.symbol}:${e.interval}:${e.rangeStart}-${e.rangeEnd}`;
        registry[key] = e;
        const sk = `${e.market}:${e.symbol}`;
        (bySymbolEntries[sk] ??= []).push(e);
      }
      const readinessBySymbol: Record<string, SymbolReadinessState> = {};
      for (const [sk, list] of Object.entries(bySymbolEntries)) {
        readinessBySymbol[sk] = deriveReadinessState(list);
      }
      return { readinessRegistry: registry, readinessBySymbol };
    }),
  addIncident: (incident) =>
    set((state) => {
      const existingIdx = state.incidents.findIndex((i) => i.incidentId === incident.incidentId);
      if (existingIdx !== -1) {
        // If the incoming incident has a resolvedAt and the stored one doesn't,
        // patch it (handles REST polling returning a pre-resolved incident).
        const existing = state.incidents[existingIdx]!;
        if (incident.resolvedAt != null && existing.resolvedAt == null) {
          const next = [...state.incidents];
          next[existingIdx] = { ...existing, resolvedAt: incident.resolvedAt };
          return { incidents: next };
        }
        return {} as Partial<IntegrityState>;
      }
      const next = [incident, ...state.incidents];
      if (next.length > 100) next.length = 100;
      return { incidents: next };
    }),
  resolveIncident: (incidentId, resolvedAt) =>
    set((state) => {
      const idx = state.incidents.findIndex((i) => i.incidentId === incidentId);
      if (idx === -1) return {} as Partial<IntegrityState>;
      const existing = state.incidents[idx]!;
      if (existing.resolvedAt != null) return {} as Partial<IntegrityState>; // already resolved
      const next = [...state.incidents];
      next[idx] = { ...existing, resolvedAt };
      return { incidents: next };
    }),
  setIncidents: (incidents) =>
    set(() => ({ incidents: incidents.slice(0, 100) })),
  setFreshness: (freshness) => set(() => ({ freshness })),
  addAlert: (alert) =>
    set((state) => {
      // Prune stale alerts as we add — the store is in front of the user
      // for hours, so without TTL the `alerts` map grows unbounded after
      // missed `integrity-resolved` events. Cheap O(n) sweep on every add.
      const cutoff = Date.now() - STALE_ALERT_TTL_MS;
      const next: Record<string, IntegrityAlert> = {};
      for (const [digest, a] of Object.entries(state.alerts)) {
        if (a.emittedAt >= cutoff) next[digest] = a;
      }
      next[alert.digest] = alert;
      return { alerts: next };
    }),
  resolveAlert: (priorDigest) =>
    set((state) => {
      const cached = state.alerts[priorDigest] ?? state.resolvedAlertCache[priorDigest];
      const nextAlerts = { ...state.alerts };
      delete nextAlerts[priorDigest];
      const patch: Partial<IntegrityState> = {
        alerts: nextAlerts,
        resolvedAt: { ...state.resolvedAt, [priorDigest]: Date.now() },
      };
      if (cached) {
        patch.resolvedAlertCache = { ...state.resolvedAlertCache, [priorDigest]: cached };
      }
      return patch;
    }),
  setAlerts: (list) =>
    set(() => {
      // Drop stale alerts from the snapshot too — the gateway-side cache
      // can serve up alerts for chunks already resolved on a different
      // replica, leaving the badge "stuck" for users who reconnect.
      const cutoff = Date.now() - STALE_ALERT_TTL_MS;
      const next: Record<string, IntegrityAlert> = {};
      for (const a of list) {
        if (a.emittedAt >= cutoff) next[a.digest] = a;
      }
      return { alerts: next };
    }),
  dismissResolved: (digest) =>
    set((state) => {
      if (!state.resolvedAt[digest] && !state.resolvedAlertCache[digest]) {
        return {} as Partial<IntegrityState>;
      }
      const nextAt = { ...state.resolvedAt };
      delete nextAt[digest];
      const nextCache = { ...state.resolvedAlertCache };
      delete nextCache[digest];
      return {
        resolvedAt: nextAt,
        resolvedAlertCache: nextCache,
      } as Partial<IntegrityState>;
    }),
  setStatuses: (symbols) =>
    set((state) => {
      // Only create a new statuses object if something actually changed
      let anyChanged = false;
      for (const s of symbols) {
        if (statusChanged(state.statuses[s.symbol], s)) {
          anyChanged = true;
          break;
        }
      }
      if (!anyChanged) {
        return {
          lastUpdateAt: Date.now(),
          lastScanType: symbols[0]?.scanType ?? state.lastScanType,
        };
      }

      const updated = { ...state.statuses };
      for (const s of symbols) {
        if (statusChanged(state.statuses[s.symbol], s)) {
          updated[s.symbol] = s;
        }
      }
      return {
        statuses: updated,
        version: state.version + 1,
        lastUpdateAt: Date.now(),
        lastScanType: symbols[0]?.scanType ?? state.lastScanType,
      };
    }),
  replaceStatuses: (symbols) =>
    set((state) => {
      const fresh: Record<string, IntegritySymbolStatus> = {};
      for (const s of symbols) {
        fresh[s.symbol] = s;
      }
      return {
        statuses: fresh,
        version: state.version + 1,
        lastUpdateAt: Date.now(),
        lastScanType: symbols[0]?.scanType ?? state.lastScanType,
      };
    }),
  setViewMode: (mode) => set({ viewMode: mode }),
  markRepairing: (symbol) =>
    set((state) => ({
      repairingSymbols: new Set(state.repairingSymbols).add(symbol),
    })),
  clearRepairing: (symbol) =>
    set((state) => {
      const next = new Set(state.repairingSymbols);
      next.delete(symbol);
      return { repairingSymbols: next };
    }),
  setRepairingAll: (v) => set({ repairingAll: v }),
  setRuns: (runs) => set({ runs: runs.slice(0, MAX_RUNS) }),
  addRun: (run) =>
    set((state) => {
      const next = [run, ...state.runs];
      if (next.length > MAX_RUNS) next.length = MAX_RUNS;
      return { runs: next };
    }),
  selectRun: (runId) => set({ selectedRunId: runId }),
  setHealth: (health) => set({ health, healthLastUpdateAt: Date.now() }),
  clearHealth: () => set({ health: null, healthLastUpdateAt: null }),
}));

/**
 * Returns true iff the symbol's readiness is fully verified (no corrupt, no
 * repair-in-progress). When we have no data for the symbol we report false —
 * the prereq strip should block trades on unknown integrity state, not
 * assume all is well.
 */
export function isSymbolReadyForTrading(
  state: Pick<IntegrityState, 'readinessBySymbol'>,
  symbol: string,
  market: string = 'spot',
): boolean {
  const key = `${market}:${symbol}`;
  const s = state.readinessBySymbol[key];
  if (!s || s.total === 0) return false;
  return s.fullyVerified && !s.anyRepairing && !s.anyCorrupt;
}

/**
 * Returns the semi-transparent red overlays the chart should paint. Filters
 * by (symbol, market, interval) and projects every non-verified range to a
 * `ChartGapRange`. Falls back to an empty array for a stable reference.
 */
export function getChartGapRanges(
  state: Pick<IntegrityState, 'readinessRegistry'>,
  symbol: string,
  interval: string,
  market: string = 'spot',
): ChartGapRange[] {
  const out: ChartGapRange[] = [];
  for (const e of Object.values(state.readinessRegistry)) {
    if (e.market !== market || e.symbol !== symbol) continue;
    if (e.interval !== interval) continue;
    if (e.repairInProgress) {
      out.push({ fromTs: e.rangeStart, toTs: e.rangeEnd, kind: 'repairing' });
      continue;
    }
    if (e.status === 'corrupt' || e.status === 'bar-invalid' || e.status === 'aggregate-value-mismatch') {
      out.push({ fromTs: e.rangeStart, toTs: e.rangeEnd, kind: 'corrupt' });
      continue;
    }
    if (e.status === 'accepted-gap' || e.status === 'accepted-outage') {
      out.push({ fromTs: e.rangeStart, toTs: e.rangeEnd, kind: 'accepted-gap' });
    }
  }
  return out.length > 0 ? out : EMPTY_RANGES;
}

/**
 * Cheap read of the raw registry slice — consumers that need all entries for
 * a symbol (across intervals) use this, then derive their own view.
 */
export function getRegistryForSymbol(
  state: Pick<IntegrityState, 'readinessRegistry'>,
  symbol: string,
  market: string = 'spot',
): ReadinessRegistryEntry[] {
  const out: ReadinessRegistryEntry[] = [];
  for (const e of Object.values(state.readinessRegistry)) {
    if (e.market === market && e.symbol === symbol) out.push(e);
  }
  return out.length > 0 ? out : EMPTY_REGISTRY;
}
